import { create } from 'zustand';
import type {
  ProjectContext,
  TechStack,
  TeamStructure,
  GitWorkflow,
  CodingStandards,
  MCPConfig,
  CICD,
} from '../types/wizard';

interface WizardState {
  currentStep: number;
  selectedPlaybook: string | null;
  projectContext: ProjectContext;
  techStack: TechStack;
  teamStructure: TeamStructure;
  gitWorkflow: GitWorkflow;
  codingStandards: CodingStandards;
  mcpConfig: MCPConfig;
  cicd: CICD;
  plugins: string[];

  // Actions
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  setSelectedPlaybook: (id: string | null) => void;
  updateProjectContext: (data: Partial<ProjectContext>) => void;
  updateTechStack: (data: Partial<TechStack>) => void;
  updateTeamStructure: (data: Partial<TeamStructure>) => void;
  updateGitWorkflow: (data: Partial<GitWorkflow>) => void;
  updateCodingStandards: (data: Partial<CodingStandards>) => void;
  updateMCPConfig: (data: Partial<MCPConfig>) => void;
  updateCICD: (data: Partial<CICD>) => void;
  updatePlugins: (plugins: string[]) => void;
  reset: () => void;
}

const initialProjectContext: ProjectContext = {
  name: '',
  goal: '',
  description: '',
  industry: '',
  compliance: [],
};

const initialTechStack: TechStack = {
  languages: [],
  frameworks: [],
  databases: [],
  cloudProvider: '',
  infrastructure: [],
  packageManager: '',
  monorepo: false,
  monorepoTool: '',
};

const initialTeamStructure: TeamStructure = {
  size: '',
  seniorityMix: { junior: 25, mid: 35, senior: 30, lead: 10 },
  claudeUsers: [],
  dedicatedReviewers: false,
  timezones: '',
};

const initialGitWorkflow: GitWorkflow = {
  branchingStrategy: '',
  branchNaming: '',
  commitStandard: '',
  signedCommits: false,
  prRequirements: [],
  protectedBranches: ['main'],
};

const initialCodingStandards: CodingStandards = {
  linting: '',
  formatting: '',
  testRequirements: [],
  coverageThreshold: 80,
  architecture: '',
  sensitivePaths: [],
  dependencyPolicy: '',
  maxFileLength: 300,
  namingConventions: [],
  customConventions: '',
};

const initialMCPConfig: MCPConfig = {
  ticketSystem: '',
  documentation: [],
  communication: [],
  sourceControl: '',
  cloudConsole: '',
  monitoring: [],
  customServers: [],
};

const initialCICD: CICD = {
  platform: '',
  stages: [],
  autoDeploy: '',
  aiCodeFlag: false,
  codeOwnership: false,
};

export const useWizardStore = create<WizardState>((set) => ({
  currentStep: 0,
  selectedPlaybook: null,
  projectContext: { ...initialProjectContext },
  techStack: { ...initialTechStack },
  teamStructure: { ...initialTeamStructure },
  gitWorkflow: { ...initialGitWorkflow },
  codingStandards: { ...initialCodingStandards },
  mcpConfig: { ...initialMCPConfig },
  cicd: { ...initialCICD },
  plugins: [],

  setStep: (step) => set({ currentStep: Math.max(0, Math.min(8, step)) }),
  setSelectedPlaybook: (id) => set({ selectedPlaybook: id }),
  nextStep: () =>
    set((state) => ({ currentStep: Math.min(8, state.currentStep + 1) })),
  prevStep: () =>
    set((state) => ({ currentStep: Math.max(0, state.currentStep - 1) })),

  updateProjectContext: (data) =>
    set((state) => ({
      projectContext: { ...state.projectContext, ...data },
    })),
  updateTechStack: (data) =>
    set((state) => ({
      techStack: { ...state.techStack, ...data },
    })),
  updateTeamStructure: (data) =>
    set((state) => ({
      teamStructure: { ...state.teamStructure, ...data },
    })),
  updateGitWorkflow: (data) =>
    set((state) => ({
      gitWorkflow: { ...state.gitWorkflow, ...data },
    })),
  updateCodingStandards: (data) =>
    set((state) => ({
      codingStandards: { ...state.codingStandards, ...data },
    })),
  updateMCPConfig: (data) =>
    set((state) => ({
      mcpConfig: { ...state.mcpConfig, ...data },
    })),
  updateCICD: (data) =>
    set((state) => ({
      cicd: { ...state.cicd, ...data },
    })),
  updatePlugins: (plugins) => set({ plugins }),

  reset: () =>
    set({
      currentStep: 0,
      selectedPlaybook: null,
      projectContext: { ...initialProjectContext },
      techStack: { ...initialTechStack },
      teamStructure: { ...initialTeamStructure },
      gitWorkflow: { ...initialGitWorkflow },
      codingStandards: { ...initialCodingStandards },
      mcpConfig: { ...initialMCPConfig },
      cicd: { ...initialCICD },
      plugins: [],
    }),
}));
